import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { pipelineApi, dealApi, usersApi, clientsApi, crmApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { CreateDealRequest, UpdateDealRequest, Lead, Client } from '@/types'

export default function DealFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { dealId } = useParams<{ dealId: string }>()
  const isEditMode = !!dealId && dealId !== 'new'

  // Form state
  const [formData, setFormData] = useState<CreateDealRequest>({
    pipeline_id: '',
    deal_name: '',
    deal_value: 0,
    currency: 'USD',
    probability: 0,
    expected_close_date: '',
    lead_id: '',
    client_id: '',
    assigned_to: '',
    notes: '',
  })

  // Queries
  const { data: pipelines = [] } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => pipelineApi.getPipelines(true),
  })

  const { data: selectedPipeline } = useQuery({
    queryKey: ['pipeline', formData.pipeline_id],
    queryFn: () => pipelineApi.getPipeline(formData.pipeline_id),
    enabled: !!formData.pipeline_id,
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  })

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getAllLegacy(),
  })

  const { data: leadsResponse } = useQuery({
    queryKey: ['leads'],
    queryFn: () => crmApi.getLeads({ page_size: 1000 }),
  })
  const leads = leadsResponse?.leads || []

  const { data: existingDeal, isLoading: isLoadingDeal } = useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => dealApi.getDeal(dealId!),
    enabled: isEditMode,
  })

  // Set default pipeline and populate form for edit mode
  useEffect(() => {
    if (isEditMode && existingDeal) {
      setFormData({
        pipeline_id: existingDeal.pipeline_id,
        stage_id: existingDeal.stage_id,
        deal_name: existingDeal.deal_name,
        deal_value: existingDeal.deal_value,
        currency: existingDeal.currency || 'USD',
        probability: existingDeal.probability,
        expected_close_date: existingDeal.expected_close_date?.split('T')[0] || '',
        lead_id: existingDeal.lead_id || '',
        client_id: existingDeal.client_id || '',
        assigned_to: existingDeal.assigned_to || '',
        notes: existingDeal.notes || '',
      })
    } else if (!isEditMode && pipelines.length > 0 && !formData.pipeline_id) {
      const defaultPipeline = pipelines.find(p => p.is_default) || pipelines[0]
      setFormData(prev => ({ ...prev, pipeline_id: defaultPipeline.pipeline_id }))
    }
  }, [isEditMode, existingDeal, pipelines, formData.pipeline_id])

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateDealRequest) => dealApi.createDeal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      toast.success(t('messages.createSuccess'))
      navigate('/crm/deals')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.createError'))),
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateDealRequest) => dealApi.updateDeal(dealId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] })
      toast.success(t('messages.updateSuccess'))
      navigate('/crm/deals')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.deal_name.trim() || !formData.pipeline_id) {
      toast.error(t('validation.required'))
      return
    }

    // Clean up empty strings for optional UUID fields to avoid backend parsing errors
    const cleanedData = {
      pipeline_id: formData.pipeline_id,
      stage_id: formData.stage_id || undefined,
      deal_name: formData.deal_name,
      deal_value: formData.deal_value,
      currency: formData.currency,
      probability: formData.probability,
      expected_close_date: formData.expected_close_date || undefined,
      lead_id: formData.lead_id || undefined,
      client_id: formData.client_id || undefined,
      assigned_to: formData.assigned_to || undefined,
      notes: formData.notes || undefined,
    }

    if (isEditMode) {
      updateMutation.mutate({
        stage_id: cleanedData.stage_id,
        deal_name: cleanedData.deal_name,
        deal_value: cleanedData.deal_value,
        currency: cleanedData.currency,
        probability: cleanedData.probability,
        expected_close_date: cleanedData.expected_close_date,
        lead_id: cleanedData.lead_id,
        client_id: cleanedData.client_id,
        assigned_to: cleanedData.assigned_to,
        notes: cleanedData.notes,
      })
    } else {
      createMutation.mutate(cleanedData)
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  if (isEditMode && isLoadingDeal) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="container max-w-3xl py-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/crm/deals')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEditMode ? t('crm.editDeal', 'Edit Deal') : t('crm.newDeal', 'New Deal')}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode ? t('crm.editDealDescription', 'Update deal information') : t('crm.newDealDescription', 'Create a new deal in your pipeline')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.basicInfo', 'Basic Information')}</CardTitle>
              <CardDescription>{t('crm.basicInfoDescription', 'Enter the deal details')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="deal_name">{t('crm.dealName', 'Deal Name')} *</Label>
                  <Input
                    id="deal_name"
                    value={formData.deal_name}
                    onChange={(e) => setFormData({ ...formData, deal_name: e.target.value })}
                    placeholder={t('crm.dealNamePlaceholder', 'e.g., Acme Corp - Website Redesign')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pipeline_id">{t('crm.pipeline', 'Pipeline')} *</Label>
                  <Select
                    value={formData.pipeline_id}
                    onValueChange={(value) => setFormData({ ...formData, pipeline_id: value, stage_id: '' })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('crm.selectPipeline', 'Select Pipeline')} />
                    </SelectTrigger>
                    <SelectContent>
                      {pipelines.map((pipeline) => (
                        <SelectItem key={pipeline.pipeline_id} value={pipeline.pipeline_id}>
                          {pipeline.pipeline_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stage_id">{t('crm.stage', 'Stage')}</Label>
                  <Select
                    value={formData.stage_id || ''}
                    onValueChange={(value) => setFormData({ ...formData, stage_id: value })}
                    disabled={!formData.pipeline_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('crm.selectStage', 'Select Stage')} />
                    </SelectTrigger>
                    <SelectContent>
                      {(selectedPipeline?.stages || [])
                        .sort((a, b) => a.display_order - b.display_order)
                        .map((stage) => (
                          <SelectItem key={stage.stage_id} value={stage.stage_id}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.stage_color }} />
                              {stage.stage_name}
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Value & Probability */}
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.valueAndProbability', 'Value & Probability')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deal_value">{t('crm.dealValue', 'Deal Value')}</Label>
                  <Input
                    id="deal_value"
                    type="number"
                    value={formData.deal_value || ''}
                    onChange={(e) => setFormData({ ...formData, deal_value: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency">{t('crm.currency', 'Currency')}</Label>
                  <Select
                    value={formData.currency || 'USD'}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="CAD">CAD</SelectItem>
                      <SelectItem value="AUD">AUD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="probability">{t('crm.probability', 'Probability')} (%)</Label>
                  <Input
                    id="probability"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.probability || ''}
                    onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expected_close_date">{t('crm.expectedCloseDate', 'Expected Close Date')}</Label>
                <Input
                  id="expected_close_date"
                  type="date"
                  value={formData.expected_close_date || ''}
                  onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Associations */}
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.associations', 'Associations')}</CardTitle>
              <CardDescription>{t('crm.associationsDescription', 'Link this deal to leads, clients, or team members')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lead_id">{t('crm.lead', 'Lead')}</Label>
                  <Select
                    value={formData.lead_id || '__none__'}
                    onValueChange={(value) => setFormData({ ...formData, lead_id: value === '__none__' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('crm.selectLead', 'Select Lead')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('common.none', 'None')}</SelectItem>
                      {leads.map((lead: Lead) => (
                        <SelectItem key={lead.lead_id} value={lead.lead_id}>
                          {lead.first_name} {lead.last_name} {lead.company_name ? `(${lead.company_name})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_id">{t('crm.client', 'Client')}</Label>
                  <Select
                    value={formData.client_id || '__none__'}
                    onValueChange={(value) => setFormData({ ...formData, client_id: value === '__none__' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('crm.selectClient', 'Select Client')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('common.none', 'None')}</SelectItem>
                      {clients.map((client: Client) => (
                        <SelectItem key={client.client_id} value={client.client_id}>
                          {client.client_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="assigned_to">{t('crm.assignedTo', 'Assigned To')}</Label>
                  <Select
                    value={formData.assigned_to || '__none__'}
                    onValueChange={(value) => setFormData({ ...formData, assigned_to: value === '__none__' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('crm.selectAssignee', 'Select Assignee')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('common.unassigned', 'Unassigned')}</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.notes', 'Notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('crm.notesPlaceholder', 'Add any additional notes about this deal...')}
                rows={4}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/crm/deals')}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Save className="mr-2 h-4 w-4" />
              {isEditMode ? t('common.save', 'Save') : t('common.create', 'Create')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

